/**
 * Page-card thumbnails — the Pages analog of state-thumbnails.ts, built on the
 * SAME shared posed-capture core (`trace-viewer/editor`): the serialized,
 * deduped queue (`enqueuePosedCardCapture` — one RTT camera page-wide) plus
 * the authored-data signature helpers (`baseSubtreeSig` / `stateOverrideSig`).
 *
 * A page card depicts the CONTAINER's subtree with exactly that page showing:
 * the pose window enables the target page node (disabling the others), applies
 * the page's shared-child state assignments instantly (prior states restored
 * after), renders, and restores. The "Shared" card (index null) depicts the
 * shared content ALONE at its base look: every page node hidden, shared
 * children reverted to Base for the render.
 *
 * FRESHNESS deliberately ignores the page nodes' ACTIVE flags: page navigation
 * writes authored `active` per page (setActive-writes-authored), so hashing it
 * would restale every card on every page switch. A card's look depends on the
 * page/shared subtrees' content, the page roots' transforms, and the card's
 * assignments — that is exactly what the hash covers.
 */

import type { SceneObject } from 'trace-viewer/scene';
import { Pages, Properties } from 'trace-viewer/behaviors';
import {
    baseSubtreeSig, stateOverrideSig, captureObjectThumbnail, enqueuePosedCardCapture, pageChildrenOf,
} from 'trace-viewer/editor';
import { EDITOR_EVENTS, type BehaviorChangedDetail } from './editor-events';
import type { PoseThumbnailProvider } from './pose-strip';
import { ThumbnailCache, fileToDataUrl } from './thumbnail-cache';

const THUMB_SIZE = 192;
const SHARED_CARD = '__shared__';

/** FIFO cap — same rationale as state-thumbnails (keys embed uniqueIds). */
const MAX_CACHE_ENTRIES = 64;

const cache = new ThumbnailCache(MAX_CACHE_ENTRIES);

function cacheKey(container: SceneObject, name: string | null): string {
    return `${container.transform.uniqueId}:pg:${name ?? SHARED_CARD}`;
}

/** The container's resolved SHARED children, in `shared` order. */
export function sharedChildrenOf(container: SceneObject): SceneObject[] {
    const shared = container.getBehavior(Pages)?.getData().shared ?? [];
    return shared
        .map(path => container.traceScene.resolveSceneObject(path, container))
        .filter((object): object is SceneObject => !!object);
}

/**
 * The part of a card hash every card shares: the shared subtrees, and for page
 * cards every page root's name, transform and content. A strip render hashes
 * every card in one synchronous pass, so this is computed once per pass (the
 * memo clears on the next microtask) instead of once per card. An edit later
 * in the same task shows on the next pass; captures and event refreshes clear
 * the memo first, so a stored hash always matches the image.
 */
const commonSigMemo = new Map<string, string>();
function commonSig(container: SceneObject, pagesToo: boolean): string {
    const key = `${container.transform.uniqueId}:${pagesToo}`;
    const memo = commonSigMemo.get(key);
    if (memo !== undefined) return memo;
    if (commonSigMemo.size === 0) queueMicrotask(() => commonSigMemo.clear());
    const parts: string[] = [];
    for (const s of sharedChildrenOf(container)) parts.push(baseSubtreeSig(s));
    if (pagesToo) {
        for (const p of pageChildrenOf(container)) {
            const t = p.getSceneObjectData().transform;
            parts.push(p.name, JSON.stringify([t?.translation, t?.rotation, t?.scale]));
            for (const c of p.getChildren()) parts.push(baseSubtreeSig(c));
        }
    }
    const sig = parts.join('|');
    commonSigMemo.set(key, sig);
    return sig;
}

/**
 * Content hash of one page card (`index` null = the Shared card). A page card
 * covers each page root's name + authored transform (NOT its active flag —
 * see module docs), the full authored signature of page content and shared
 * subtrees, its assignments, and the assigned states' overrides on the
 * targets. The Shared card depends only on the shared subtrees.
 */
function pageCardHash(container: SceneObject, pages: Pages, index: number | null): string {
    const data = pages.getData();
    const parts: string[] = [commonSig(container, index !== null)];
    if (index !== null) {
        const assignments = data.pages?.[index]?.states ?? [];
        parts.push(JSON.stringify(assignments));
        for (const a of assignments) {
            const obj = container.traceScene?.resolveSceneObject(a.target, container);
            if (obj) parts.push(stateOverrideSig(obj, a.state));
        }
    }
    return (index === null ? 'sh:' : `pg${index}:`) + parts.join('|');
}

/**
 * Pose hook showing exactly page `index` (null = the Shared card: every page
 * hidden, shared children at Base): page nodes' enabled flags are set
 * directly on the TransformNodes (no authored write), and states are instant
 * editor picks with per-target prior-pick restore — mirroring
 * captureStateCard's snapshot-restore contract. A pick shows over the strip's
 * pick and over the runtime slot; the shared card's `null` pick shows Base
 * whatever the runtime slot holds. The window is synchronous (apply → render
 * → restore, no awaits). Exported for tests.
 */
export function pagePose(container: SceneObject, pages: Pages, index: number | null): () => () => void {
    const ignore = () => {};
    return () => {
        const nodes = pages.getPageNodes();
        const prevEnabled = nodes.map(n => n.isEnabled(false));
        nodes.forEach((n, i) => n.setEnabled(index !== null && i === index));

        const restores: (() => void)[] = [];
        const applyState = (props: Properties, state: string | null) => {
            const prior = props.getEditorState();
            try { props.setEditorState(state, true); } catch { ignore(); }
            restores.push(() => { try { props.setEditorState(prior, true); } catch { ignore(); } });
        };
        if (index !== null) {
            const assignments = pages.getData().pages?.[index]?.states ?? [];
            for (const a of assignments) {
                const obj = container.traceScene?.resolveSceneObject(a.target, container);
                const props = obj?.getBehavior(Properties);
                if (!props || !props.getStateNames().includes(a.state)) continue;
                applyState(props, a.state);
            }
        } else {
            // Shared card: depict shared content at its BASE look — the live
            // scene may have the current page's states applied to it.
            for (const s of sharedChildrenOf(container)) {
                for (const o of [s, ...s.getDescendants()]) {
                    const props = o.getBehavior(Properties);
                    if (props && props.getActiveState() !== undefined) applyState(props, null);
                }
            }
        }
        return () => {
            for (const r of restores.reverse()) r();
            nodes.forEach((n, i) => n.setEnabled(prevEnabled[i]));
        };
    };
}

function enqueueCapture(container: SceneObject, pages: Pages, name: string | null): void {
    const key = cacheKey(container, name);
    const indexOf = (): number | null => {
        if (name === null) return null;
        const idx = pageChildrenOf(container).findIndex(p => p.name === name);
        return idx >= 0 ? idx : -1;
    };
    const idx = indexOf();
    if (idx === -1) return; // renamed/deleted page — nothing to capture
    enqueuePosedCardCapture({
        owner: container,
        key,
        computeHash: () => pageCardHash(container, pages, idx),
        isFresh: (hash) => cache.isFresh(key, hash),
        capture: async () => {
            commonSigMemo.clear(); // store the hash of the scene this renders
            const hash = pageCardHash(container, pages, idx);
            const file = await captureObjectThumbnail(container, {
                size: THUMB_SIZE,
                pose: pagePose(container, pages, idx),
            });
            if (!file) {
                // Nothing to depict (e.g. the Shared group just emptied) — the
                // rig can't produce an image, so store a BLANK entry under the
                // new hash: the card drops the stale image for the placeholder
                // and freshness stops re-enqueuing a capture that cannot land.
                cache.set(key, { dataUrl: '', hash });
                return null;
            }
            return { file, hash };
        },
        onCaptured: async ({ file, hash }) => {
            cache.set(key, { dataUrl: await fileToDataUrl(file), hash });
        },
    });
}

// One stable provider per container uniqueId (matches state-thumbnails).
const providers = new Map<number, PoseThumbnailProvider>();

/** Build (or reuse) the PoseThumbnailProvider for a Pages container's cards. */
export function makePageThumbnailProvider(container: SceneObject, pages: Pages): PoseThumbnailProvider {
    const id = container.transform.uniqueId;
    const allCards = (): (string | null)[] => [null, ...pageChildrenOf(container).map(p => p.name)];

    const existing = providers.get(id);
    // The page context rebuilds its adapter on a 500 ms poll, so this branch
    // must stay cheap: no card is hashed here. A shown card re-checks its own
    // freshness in getThumbnail, and edits refresh through onBehaviorChanged.
    if (existing) return existing;

    const refresh = () => {
        commonSigMemo.clear(); // the edit may have landed after a hash this task
        for (const name of allCards()) enqueueCapture(container, pages, name);
        cache.notify();
    };

    // Pages commits (assignments, shared list, reorder) and Properties commits
    // anywhere under the container (state edits on shared children, content
    // property edits) can change a card without changing the strip's own poll
    // signals — refresh on either targeting the container's subtree.
    const onBehaviorChanged = (event: Event) => {
        const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
        if (detail?.behaviorType !== 'Properties' && detail?.behaviorType !== 'Pages') return;
        const path = detail.objectPath;
        if (!path) return;
        const changed = container.traceScene?.resolveSceneObject(path);
        let cur: SceneObject | null = changed ?? null;
        while (cur) {
            if (cur === container) { refresh(); return; }
            cur = cur.getParent();
        }
    };
    window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);

    const provider: PoseThumbnailProvider = {
        getThumbnail(name: string | null): string | undefined {
            const key = cacheKey(container, name);
            const entry = cache.get(key);
            const idx = name === null
                ? null
                : pageChildrenOf(container).findIndex(p => p.name === name);
            if (idx === -1) return entry?.dataUrl;
            if (!entry || entry.hash !== pageCardHash(container, pages, idx)) {
                enqueueCapture(container, pages, name);
            }
            return entry?.dataUrl;
        },
        subscribe(callback: () => void): () => void {
            return cache.subscribe(callback);
        },
    };
    providers.set(id, provider);
    for (const name of allCards()) enqueueCapture(container, pages, name);
    return provider;
}
